import {
  Button,
  Card,
  CardBody,
  CircularProgress,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  Link,
  Stack,
  Text,
} from '@chakra-ui/react';
import { IoCopyOutline } from 'react-icons/io5';
import { MdInfoOutline } from 'react-icons/md';
import { IMaskInput } from 'react-imask';
import { copyToClipboard } from 'shared/helpers/clipboard';
import { cepMask, documentMasks, phoneMasks } from 'shared/utils/maskOptions';
import { useCreateCustomerModalContext } from './ModalContext';

interface CreateCustomerModalProps {
  isOpen: boolean;
  onClose(): void;
}

const CreateCustomerModal: React.FC<CreateCustomerModalProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    errors,
    handleSubmit,
    loading,
    cnpj,
    name,
    contact_email,
    trade_name,
    state_registration,
    city_registration,
    postal_code,
    logradouro,
    numero,
    complemento,
    bairro,
    telephone,
    setCnpj,
    setName,
    setContactEmail,
    setTradeName,
    setPostalCode,
    setAddress,
    setNumber,
    setComplement,
    setNeighborhood,
    setPhone,
    lockDocument,
    invalidCep,
    warnings,
  } = useCreateCustomerModalContext();

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
      <DrawerOverlay />
      <DrawerContent pos="relative">
        <DrawerCloseButton />
        <DrawerHeader>
          <Heading size="md">Novo Cliente</Heading>

          <Stack mt="1rem">
            {warnings.map(({ field, idi }) => (
              <Card bg="yooga.50" color="yooga.500">
                <CardBody
                  as={HStack}
                  py="0.5rem"
                  justifyContent="space-between"
                  wrap="wrap"
                >
                  <Text fontSize="1rem" fontWeight="400">
                    Este {field} está associado ao IDI {idi}
                  </Text>
                  <IconButton
                    icon={<IoCopyOutline />}
                    aria-label="Copiar IDI"
                    colorScheme="yooga"
                    size="sm"
                    onClick={() => copyToClipboard(idi, 'IDI copiado')}
                  />
                </CardBody>
              </Card>
            ))}
          </Stack>
        </DrawerHeader>

        <DrawerBody>
          <Heading size="sm" mb="1rem">
            Dados da empresa
          </Heading>
          <Flex columnGap="5%" rowGap="1rem" wrap="wrap">
            <FormControl
              isRequired
              isInvalid={!!errors.cnpj}
              flexBasis="37.5%"
              isReadOnly={lockDocument}
            >
              <FormLabel fontSize="0.8rem" color="gray.500">
                CPF/CNPJ
              </FormLabel>
              <Input
                as={IMaskInput}
                placeholder="000.000.000-00"
                mask={documentMasks}
                value={cnpj}
                onAccept={(value: string) => setCnpj(value)}
                type="text"
              />
              {errors.cnpj && (
                <FormErrorMessage>{errors.cnpj}</FormErrorMessage>
              )}
            </FormControl>

            <FormControl
              isRequired
              isInvalid={!!errors.trade_name}
              flexBasis="57.5%"
            >
              <FormLabel fontSize="0.8rem" color="gray.500">
                Razão Social
              </FormLabel>
              <Input
                value={trade_name}
                placeholder="Yooga Tecnologia"
                onChange={(e) => setTradeName(e.target.value)}
                type="text"
              />
              {errors.trade_name && (
                <FormErrorMessage>{errors.trade_name}</FormErrorMessage>
              )}
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.name} flexBasis="47.5%">
              <FormLabel fontSize="0.8rem" color="gray.500">
                Nome Fantasia
              </FormLabel>
              <Input
                placeholder="Yooga Tecnologia S/A"
                value={name}
                onChange={(e) => setName(e.target.value)}
                type="text"
              />
              {errors.name && (
                <FormErrorMessage>{errors.name}</FormErrorMessage>
              )}
            </FormControl>

            <FormControl
              isRequired
              isInvalid={!!errors.contact_email}
              flexBasis="47.5%"
            >
              <FormLabel fontSize="0.8rem" color="gray.500">
                E-mail
              </FormLabel>
              <Input
                placeholder="exemplo@mail.com"
                value={contact_email}
                onChange={(e) => setContactEmail(e.target.value)}
                type="email"
              />
              {errors.contact_email && (
                <FormErrorMessage>{errors.contact_email}</FormErrorMessage>
              )}
            </FormControl>

            <FormControl
              isRequired
              isInvalid={!!errors.telephone}
              flexBasis="47.5%"
            >
              <FormLabel fontSize="0.8rem" color="gray.500">
                Telefone
              </FormLabel>
              <Input
                as={IMaskInput}
                placeholder="(00) 98765-4321"
                mask={phoneMasks}
                value={telephone}
                onAccept={(value: string) => setPhone(value)}
                type="text"
              />
              {errors.telephone && (
                <FormErrorMessage>{errors.telephone}</FormErrorMessage>
              )}
            </FormControl>
          </Flex>

          <Divider my="2rem" />

          <Heading size="sm" mb="1rem">
            Endereço
          </Heading>
          <Flex columnGap="5%" rowGap="1rem" wrap="wrap">
            <HStack flexBasis="100%" align="flex-end" gap="1rem">
              <FormControl
                isRequired
                isInvalid={!!errors.postal_code}
                flexBasis="30%"
                flexShrink={0}
              >
                <FormLabel fontSize="0.8rem" color="gray.500">
                  CEP
                </FormLabel>
                <Input
                  as={IMaskInput}
                  mask={cepMask}
                  value={postal_code}
                  onAccept={(value: string) => setPostalCode(value)}
                  type="text"
                />
                {errors.postal_code && (
                  <FormErrorMessage>{errors.postal_code}</FormErrorMessage>
                )}
              </FormControl>

              {invalidCep && (
                <Card bg="yellow.100" flex="1" h="40px" justifyContent="center">
                  <CardBody py="0" px="1rem" display="flex" alignItems="center">
                    <HStack gap="0.5rem">
                      <Icon as={MdInfoOutline} color="yellow.500" />
                      <Text fontSize="0.8rem" fontWeight="400" color="gray.600">
                        <strong>CEP inexistente.</strong> Busque o correto em:{' '}
                        <Link
                          href="https://buscacepinter.correios.com.br/app/endereco/index.php"
                          isExternal
                          color="blue.600"
                          textDecoration="underline"
                        >
                          Correios
                        </Link>
                        {' / '}
                        <Link
                          href="https://busca-cep-aqui.base44.app"
                          isExternal
                          color="blue.600"
                          textDecoration="underline"
                        >
                          ViaCep
                        </Link>
                      </Text>
                    </HStack>
                  </CardBody>
                </Card>
              )}
            </HStack>

            <FormControl
              isRequired
              isInvalid={!!errors.logradouro}
              flexBasis="100%"
            >
              <FormLabel fontSize="0.8rem" color="gray.500">
                Logradouro
              </FormLabel>
              <Input
                value={logradouro}
                onChange={(e) => setAddress(e.target.value)}
                type="text"
              />
              {errors.logradouro && (
                <FormErrorMessage>{errors.logradouro}</FormErrorMessage>
              )}
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.numero} flexBasis="30%">
              <FormLabel fontSize="0.8rem" color="gray.500">
                Número
              </FormLabel>
              <Input
                value={numero}
                onChange={(e) => setNumber(e.target.value)}
                type="number"
              />
              {errors.numero && (
                <FormErrorMessage>{errors.numero}</FormErrorMessage>
              )}
            </FormControl>

            <FormControl flexBasis="65%">
              <FormLabel fontSize="0.8rem" color="gray.500">
                Complemento
              </FormLabel>
              <Input
                value={complemento}
                onChange={(e) => setComplement(e.target.value)}
                type="text"
              />
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.bairro} flexBasis="30%">
              <FormLabel fontSize="0.8rem" color="gray.500">
                Bairro
              </FormLabel>
              <Input
                value={bairro}
                onChange={(e) => setNeighborhood(e.target.value)}
                type="text"
              />
              {errors.bairro && (
                <FormErrorMessage>{errors.bairro}</FormErrorMessage>
              )}
            </FormControl>

            <FormControl
              isRequired
              isInvalid={!!errors.city_registration}
              flexBasis="30%"
              isReadOnly
            >
              <FormLabel fontSize="0.8rem" color="gray.500">
                Cidade
              </FormLabel>
              <Input value={city_registration} type="text" bg="gray.50" />
              {errors.city_registration && (
                <FormErrorMessage>{errors.city_registration}</FormErrorMessage>
              )}
            </FormControl>

            <FormControl
              isRequired
              isInvalid={!!errors.state_registration}
              flexBasis="30%"
              isReadOnly
            >
              <FormLabel fontSize="0.8rem" color="gray.500">
                UF
              </FormLabel>
              <Input value={state_registration} type="text" bg="gray.50" />
              {errors.state_registration && (
                <FormErrorMessage>{errors.state_registration}</FormErrorMessage>
              )}
            </FormControl>
          </Flex>
        </DrawerBody>

        <DrawerFooter>
          <Button
            colorScheme="yooga"
            onClick={handleSubmit}
            w="100%"
            isDisabled={loading || invalidCep}
          >
            Salvar
          </Button>
        </DrawerFooter>

        {loading && (
          <Flex
            pos="absolute"
            top="0"
            left="0"
            w="100%"
            h="100%"
            bg="gray.100"
            zIndex="1"
            align="center"
            justify="center"
          >
            <CircularProgress isIndeterminate color="yooga.400" />
          </Flex>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default CreateCustomerModal;
